import { AttachmentSlot } from '@dropbox/sign-internal-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSignerAppClient } from 'signer-app/context/signer-app-client';
import { SignatureRequestResponse } from 'signer-app/signer-experience/types';
import { getCSRFToken } from 'signer-app/utils/hs-fetch';
import { signerExperienceQueryKey } from './types';

type UseUploadAttachmentParams = {
  signatureRequestId: string;
  accessCode: string | null;
};

export function useUploadAttachment({
  signatureRequestId,
  accessCode,
}: UseUploadAttachmentParams) {
  const client = useSignerAppClient();
  const queryClient = useQueryClient();

  const queryKey = signerExperienceQueryKey(signatureRequestId, accessCode);

  const { mutate: uploadAttachment } = useMutation<
    unknown,
    unknown,
    {
      attachmentSlotId: string;
      file: File;
    }
  >({
    mutationKey: [signatureRequestId, accessCode, 'upload-attachment'] as const,
    mutationFn: async ({ attachmentSlotId, file }) => {
      const fileName = file.name;
      await client.signerApi.uploadAttachment(attachmentSlotId, {
        file,
        csrfToken: getCSRFToken(),
      });
      const uploadedAt = String(Date.now());
      queryClient.setQueryData(
        queryKey,
        (
          prev: SignatureRequestResponse | undefined,
        ): SignatureRequestResponse | undefined => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            signatureRequest: {
              ...prev.signatureRequest,
              attachmentSlots: prev.signatureRequest.attachmentSlots.map(
                (slot): AttachmentSlot => {
                  if (slot.id === attachmentSlotId) {
                    return {
                      ...slot,
                      uploadedAt,
                      fileName,
                    };
                  }
                  return slot;
                },
              ),
            },
          };
        },
      );
    },
  });

  return { uploadAttachment };
}
